import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'

const KEY = 'sakk.banner.dismissedAt'
const HIDE_DAYS = 7
const HEIGHT = 52

function wasDismissed(): boolean {
  try {
    const at = Number(localStorage.getItem(KEY) || 0)
    return at > 0 && Date.now() - at < HIDE_DAYS * 86_400_000
  } catch {
    return false
  }
}

/**
 * Top announcement bar: logo + app name + a one-tap download. It reserves its height through the
 * `--banner-h` CSS variable (the fixed navbar and the page content shift down by it) and stays hidden
 * for a week once closed.
 */
export function AppBanner() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(() => !wasDismissed())

  useEffect(() => {
    document.documentElement.style.setProperty('--banner-h', open ? `${HEIGHT}px` : '0px')
    return () => document.documentElement.style.setProperty('--banner-h', '0px')
  }, [open])

  const close = () => {
    try {
      localStorage.setItem(KEY, String(Date.now()))
    } catch {
      /* private mode: just hide for this visit */
    }
    setOpen(false)
  }

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="app-banner"
          role="region"
          aria-label={t('banner.label')}
          initial={{ y: -HEIGHT }}
          animate={{ y: 0 }}
          exit={{ y: -HEIGHT }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed top-0 inset-x-0 z-[60] bg-wine-800 text-marble-50"
          style={{ height: HEIGHT }}
        >
          <div className="mx-auto flex h-full max-w-6xl items-center gap-3 px-3 sm:px-4">
            <img src="/logo.svg" alt="" width={36} height={36} className="h-9 w-9 shrink-0 rounded-[10px]" />
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-[14px] font-extrabold">
                {t('banner.title')}
                <span className="ms-2 hidden font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-marble-50/70 sm:inline">SAKK WALLET</span>
              </p>
              <p className="truncate text-[12px] text-marble-50/80">{t('banner.subtitle')}</p>
            </div>
            <a
              href="/download"
              download="sakk-wallet.apk"
              className="inline-flex h-9 shrink-0 items-center rounded-full bg-marble-50 px-4 text-[13px] font-bold text-wine-800 transition-colors hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-marble-50"
            >
              {t('banner.cta')}
            </a>
            <button
              type="button"
              onClick={close}
              aria-label={t('banner.close')}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-marble-50/80 transition-colors hover:bg-white/10 hover:text-marble-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-marble-50"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
